import { create } from "zustand";

const normalizeAttributeName = (value) => String(value ?? '').trim().toLowerCase();

function normalizeColorName(color) {
  return String(color ?? '').trim().toLowerCase();
}

function getVariantAttributeValue(variant, name) {
  const attribute = (variant?.attributes ?? []).find(
    (item) => normalizeAttributeName(item.name) === normalizeAttributeName(name)
  );
  return String(attribute?.value ?? '').trim();
}

export function extractImageUrl(image) {
  if (!image) return '';
  if (typeof image === 'string') return image.trim();
  if (typeof image === 'object') {
    return String(image.url || image.src || image.image_url || '').trim();
  }
  return '';
}

export function getVariantImageUrls(variant) {
  if (!variant) return [];
  if (Array.isArray(variant.images) && variant.images.length > 0) {
    return variant.images.map(extractImageUrl).filter(Boolean);
  }
  const singleImage = extractImageUrl(variant.image) || extractImageUrl(variant.image_url);
  if (singleImage) return [singleImage];
  return [];
}

export function getVariantColorName(variant) {
  const colorAttr = (variant?.attributes ?? []).find(
    (attr) => normalizeAttributeName(attr.name) === 'color'
  );
  return colorAttr ? String(colorAttr.value ?? '').trim() : '';
}

function getImageKey(url) {
  try {
    const filename = decodeURIComponent(url.split('/').pop());
    return filename.replace(/-\d{8}T\d{6}\.\d+(?=\.[^.]+$)/, '');
  } catch {
    return url;
  }
}

export function getProductGalleryImages(product, selectedColor = '', selectedSize = '') {
  if (!product) return [];

  const variants = Array.isArray(product.variants) ? product.variants : [];
  const matchingVariants = variants.filter((variant) => {
    const matchesColor = !selectedColor || normalizeColorName(getVariantColorName(variant)) === normalizeColorName(selectedColor);
    const matchesSize = !selectedSize || normalizeAttributeName(getVariantAttributeValue(variant, 'size')) === normalizeAttributeName(selectedSize);
    return matchesColor && matchesSize;
  });
  const variantUrls = matchingVariants.flatMap(getVariantImageUrls).filter(Boolean);
  const fallbackUrls = [
    ...(Array.isArray(product.images) ? product.images.map(extractImageUrl) : []),
    extractImageUrl(product.image),
    extractImageUrl(product.image_url),
  ].filter(Boolean);
  const urls = variantUrls.length > 0 ? variantUrls : fallbackUrls;

  return [...new Map(urls.map((url) => [getImageKey(url), url])).values()];
}

export function getProductVariantImage(product, selectedVariant = null, selectedColor = '') {
  if (!product) return '';

  // 1. Direct selected variant image
  if (selectedVariant) {
    const variantImages = getVariantImageUrls(selectedVariant);
    if (variantImages.length > 0) {
      return variantImages[0];
    }
  }

  // Target color to match
  const targetColor = selectedColor || (selectedVariant ? getVariantColorName(selectedVariant) : '');

  // 2. Fallback to another variant sharing the same color
  if (targetColor) {
    const normalizedTarget = normalizeColorName(targetColor);
    const sameColorVariantWithImage = (product.variants || []).find((variant) => {
      const vColor = getVariantColorName(variant);
      if (normalizeColorName(vColor) !== normalizedTarget) return false;
      const images = getVariantImageUrls(variant);
      return images.length > 0;
    });

    if (sameColorVariantWithImage) {
      const images = getVariantImageUrls(sameColorVariantWithImage);
      if (images.length > 0) return images[0];
    }
  }

  // 3. Fallback to any variant with an image
  const anyVariantWithImage = (product.variants || []).find((variant) => {
    const images = getVariantImageUrls(variant);
    return images.length > 0;
  });
  if (anyVariantWithImage) {
    const images = getVariantImageUrls(anyVariantWithImage);
    if (images.length > 0) return images[0];
  }

  // 4. Product-level image
  if (Array.isArray(product.images) && product.images.length > 0) {
    const url = extractImageUrl(product.images[0]);
    if (url) return url;
  }
  const mainImageUrl = extractImageUrl(product.image) || extractImageUrl(product.image_url);
  if (mainImageUrl) return mainImageUrl;

  return '';
}

export function buildColorImageMap(product) {
  const map = {};
  if (!product || !Array.isArray(product.variants)) return map;

  product.variants.forEach((variant) => {
    const color = getVariantColorName(variant);
    if (!color) return;
    const key = normalizeColorName(color);
    const urls = getVariantImageUrls(variant);
    if (urls.length > 0) {
      if (!map[key]) map[key] = [];
      urls.forEach((url) => {
        if (!map[key].includes(url)) map[key].push(url);
      });
    }
  });

  return map;
}

export function enrichProduct(product) {
  if (!product) return product;

  const colorImageMap = buildColorImageMap(product);

  return {
    ...product,
    colorImageMap,
    getImageForColor: (color) => {
      const key = normalizeColorName(color);
      return colorImageMap[key]?.[0] || getProductVariantImage(product, null, color);
    },
    getImageForVariant: (variant, color) => getProductVariantImage(product, variant, color),
  };
}

const useProductStore = create((set, get) => ({
  products: [],

  setProducts: (products) =>
    set({
      products: (Array.isArray(products) ? products : []).map(enrichProduct),
    }),

  updateProduct: (product) =>
    set((state) => {
      if (!product) return state;
      const enriched = enrichProduct(product);
      const exists = state.products.some((p) => p.id === product.id);
      return {
        products: exists
          ? state.products.map((p) => (p.id === product.id ? { ...p, ...enriched } : p))
          : [...state.products, enriched],
      };
    }),

  getProductImage: (productOrId, selectedVariant = null, selectedColor = '') => {
    const state = get();
    const product = typeof productOrId === 'object' && productOrId !== null
      ? productOrId
      : state.products.find((p) => p.id === productOrId);
    return getProductVariantImage(product, selectedVariant, selectedColor);
  },

  getColorImages: (productOrId, selectedColor) => {
    const state = get();
    const product = typeof productOrId === 'object' && productOrId !== null
      ? productOrId
      : state.products.find((p) => p.id === productOrId);
    if (!product) return [];
    const map = product.colorImageMap || buildColorImageMap(product);
    const key = normalizeColorName(selectedColor);
    return map[key] || [];
  },
}));

export default useProductStore;